import { useMemo, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  CrownIcon,
  GitBranchIcon,
  Search01Icon,
  Tick02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Link, useNavigate } from "react-router-dom";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  InputSize
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { cx } from "shared/utils";
import { Template } from "types/landlord/verificationTemplate";

export const ROUTING_SECTION_PARAM = "routing";

type RouteItem = {
  id: string;
  name: string;
  isMain: boolean;
  answers: string[];
};

const buildItems = (template: Template, mainTemplate: Template) => {
  const answersByTemplate = new Map<string, string[]>();
  (mainTemplate.routes ?? []).forEach((route) => {
    const target = route.target_template_identifier ?? mainTemplate.id;
    answersByTemplate.set(target, [
      ...(answersByTemplate.get(target) ?? []),
      route.label
    ]);
  });

  const items: RouteItem[] = [
    {
      id: mainTemplate.id,
      name: mainTemplate.name,
      isMain: true,
      answers: answersByTemplate.get(mainTemplate.id) ?? []
    },
    ...(mainTemplate.variants ?? []).map((variant) => ({
      id: variant.id,
      name: variant.name,
      isMain: false,
      answers: answersByTemplate.get(variant.id) ?? []
    }))
  ];
  if (!items.some((item) => item.id === template.id))
    items.push({
      id: template.id,
      name: template.name,
      isMain: false,
      answers: []
    });
  return items;
};

const describeAnswers = (answers: string[]) => {
  if (!answers.length) return "Not used by any answer";
  if (answers.length === 1) return `Answer: ${answers[0]}`;
  return `${answers.length} answers: ${answers.join(", ")}`;
};

const matchesQuery = (item: RouteItem, query: string) =>
  [item.name, ...item.answers].some((text) =>
    text.toLowerCase().includes(query)
  );

const GroupHeading = ({ children }: { children: string }) => (
  <p className="px-3 pb-1 pt-2 text-xxs font-semibold uppercase tracking-wide text-content-400">
    {children}
  </p>
);

const RouteOption = ({
  item,
  isCurrent,
  onSelect
}: {
  item: RouteItem;
  isCurrent: boolean;
  onSelect: () => void;
}) => (
  <Link
    to={`/verification-templates/${item.id}`}
    title={item.name}
    aria-current={isCurrent ? "page" : undefined}
    onClick={onSelect}
    className={cx(
      "flex min-w-0 items-center gap-2.5 rounded-md px-3 py-2",
      isCurrent ? "bg-primary-20" : "hover:bg-alpha-40"
    )}
  >
    <HugeiconsIcon
      icon={item.isMain ? CrownIcon : GitBranchIcon}
      size={16}
      className={cx(
        "shrink-0",
        item.isMain ? "text-content-400" : "text-primary-500"
      )}
    />
    <span className="min-w-0 flex-1">
      <span
        className={cx(
          "block truncate text-sm font-semibold",
          isCurrent ? "text-primary-600" : "text-content-1000"
        )}
      >
        {item.name}
      </span>
      <span className="block truncate text-xs text-content-400">
        {describeAnswers(item.answers)}
      </span>
    </span>
    {isCurrent ? (
      <HugeiconsIcon
        icon={Tick02Icon}
        size={16}
        className="shrink-0 text-primary-500"
      />
    ) : null}
  </Link>
);

export const TemplateRouteSwitcher = ({
  template,
  parentTemplate
}: {
  template: Template;
  parentTemplate?: Template;
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const mainTemplate = template.parent_template_identifier
    ? parentTemplate
    : template;

  const items = useMemo(
    () => (mainTemplate ? buildItems(template, mainTemplate) : []),
    [template, mainTemplate]
  );

  const filteredItems = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return normalized
      ? items.filter((item) => matchesQuery(item, normalized))
      : items;
  }, [items, query]);

  if (
    mainTemplate?.routing_question == null ||
    mainTemplate.routing_enabled === false
  )
    return null;

  const question = mainTemplate.routing_question || "Template routing";
  const current = items.find((item) => item.id === template.id);
  const variants = items.filter((item) => !item.isMain);

  const changeOpen = (value: boolean) => {
    setOpen(value);
    if (!value) setQuery("");
  };

  const renderOption = (item: RouteItem) => (
    <RouteOption
      key={item.id}
      item={item}
      isCurrent={item.id === template.id}
      onSelect={() => changeOpen(false)}
    />
  );

  return (
    <div className="mt-3 rounded-lg border border-alpha-100 bg-background-900 px-3 pb-3 pt-2.5">
      <div className="flex items-center gap-2">
        <HugeiconsIcon
          icon={GitBranchIcon}
          size={16}
          className="shrink-0 text-content-400"
        />
        <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-content-400">
          Routing · {items.length} templates
        </span>
        <Button
          type="button"
          className="shrink-0"
          size={ButtonSize.XS}
          variant={ButtonVariant.TEXT}
          onClick={() =>
            navigate(
              `/verification-templates/${mainTemplate.id}?section=${ROUTING_SECTION_PARAM}`
            )
          }
        >
          Edit
        </Button>
      </div>

      <p
        title={question}
        className="mt-1 line-clamp-2 break-words text-sm font-semibold text-content-1000"
      >
        {question}
      </p>

      <p className="mb-1 mt-2 text-xs text-content-400">Viewing template</p>

      <Popover placement="bottom-start" open={open} onOpenChange={changeOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => changeOpen(!open)}
            className={cx(
              "flex w-full min-w-0 items-center gap-2 rounded-md border bg-white px-3 py-2 text-left shadow-xs",
              open
                ? "border-primary-100 shadow-focus-ring"
                : "border-alpha-100 hover:border-alpha-200"
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 items-center gap-2">
                <span
                  title={current?.name}
                  className="min-w-0 flex-1 truncate text-sm font-semibold text-content-1000"
                >
                  {current?.name ?? template.name}
                </span>
                <Badge
                  size={BadgeSize.SM}
                  variant={
                    current?.isMain ? BadgeVariant.BASE : BadgeVariant.PRIMARY
                  }
                  className="shrink-0"
                >
                  {current?.isMain ? "Main" : "Variant"}
                </Badge>
              </span>
              <span className="block truncate text-xs text-content-400">
                {describeAnswers(current?.answers ?? [])}
              </span>
            </span>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              size={16}
              className={cx(
                "shrink-0 text-content-400 transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        </PopoverTrigger>

        <PopoverContent className="z-50 w-[330px] max-w-[calc(100vw-24px)] overflow-hidden rounded-lg border border-alpha-100 bg-white shadow-lg">
          <div className="border-b border-alpha-40 p-2">
            <BaseInput
              id="template-route-switcher-search"
              label="Search templates"
              labelClassName="sr-only"
              hideOptional
              size={InputSize.SM}
              Icon={Search01Icon}
              placeholder="Search templates or answers"
              value={query}
              onChange={(value: string) => setQuery(value)}
            />
          </div>

          <div className="max-h-[300px] overflow-y-auto p-1">
            {query.trim() ? (
              <>
                <p className="px-3 pb-1 pt-2 text-xs text-content-400">
                  {filteredItems.length} of {items.length} templates
                </p>
                {filteredItems.map(renderOption)}
                {filteredItems.length ? null : (
                  <p className="px-3 py-3 text-sm text-content-400">
                    No template matches “{query.trim()}”.
                  </p>
                )}
              </>
            ) : (
              <>
                <GroupHeading>Main template</GroupHeading>
                {items.filter((item) => item.isMain).map(renderOption)}
                {variants.length ? (
                  <>
                    <GroupHeading>Variants</GroupHeading>
                    {variants.map(renderOption)}
                  </>
                ) : null}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
